import * as React from 'react';
import { toast } from 'sonner';
import { capturePostHog } from '@/lib/posthog';
import { UserPlus, Dumbbell, LayoutDashboard, ArrowRight, CheckCircle } from 'lucide-react';
import { getSupabase } from '@/lib/supabase';
import { hydrateSessionFromWorkerCookies } from '@/utils/syncSession';
import { consumeStoredPlanIntent, planIntentFromUserMetadata, type PlanIntent } from '@/utils/planIntent';
import { startCheckout } from '@/utils/startCheckout';
import { updateProfile } from '@/data/settingsApi';
import { syncSupabaseSession } from '@/data/authApi';
import { reportSwallowed } from '@/lib/sentry';

/**
 * Push the freshly exchanged session tokens into our Worker cookie jar.
 *
 * Called after `supabase.auth.updateUser(...)` in handleContinue / handleSkip.
 * Without this the user can land on `/` with Supabase-side auth but no
 * Worker cookies, which causes a ~1s flash of the sign-in page before the
 * SPA re-authenticates.
 */
async function syncWorkerSession(): Promise<void> {
  // Best effort (never throws); the dashboard self-heals if this misses.
  await syncSupabaseSession(getSupabase());
}

// ─── Timezone list ───────────────────────────────────────────────────────────
const TIMEZONES = [
  'Pacific/Honolulu',
  'America/Anchorage',
  'America/Los_Angeles',
  'America/Denver',
  'America/Phoenix',
  'America/Chicago',
  'America/New_York',
  'America/Toronto',
  'America/Vancouver',
  'America/Mexico_City',
  'America/Bogota',
  'America/Lima',
  'America/Santiago',
  'America/Sao_Paulo',
  'America/Argentina/Buenos_Aires',
  'Atlantic/Reykjavik',
  'Europe/London',
  'Europe/Lisbon',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Amsterdam',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Stockholm',
  'Europe/Warsaw',
  'Europe/Athens',
  'Europe/Istanbul',
  'Africa/Cairo',
  'Africa/Lagos',
  'Africa/Nairobi',
  'Africa/Johannesburg',
  'Asia/Riyadh',
  'Asia/Dubai',
  'Asia/Tehran',
  'Asia/Karachi',
  'Asia/Kolkata',
  'Asia/Dhaka',
  'Asia/Bangkok',
  'Asia/Ho_Chi_Minh',
  'Asia/Singapore',
  'Asia/Jakarta',
  'Asia/Shanghai',
  'Asia/Hong_Kong',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Australia/Perth',
  'Australia/Adelaide',
  'Australia/Brisbane',
  'Australia/Sydney',
  'Pacific/Auckland',
  'UTC',
];

function getTimezoneOptions(): string[] {
  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return TIMEZONES.includes(browserTz) ? TIMEZONES : [browserTz, ...TIMEZONES];
}

function formatTz(tz: string): string {
  try {
    const offset = new Intl.DateTimeFormat('en', {
      timeZone: tz,
      timeZoneName: 'shortOffset',
    })
      .formatToParts()
      .find((p) => p.type === 'timeZoneName')?.value;
    return `${offset ? `(${offset}) ` : ''}${tz.replace(/_/g, ' ')}`;
  } catch {
    return tz.replace(/_/g, ' ');
  }
}

// ─── Component ───────────────────────────────────────────────────────────────
export default function CoachOnboarding() {
  const [step, setStep] = React.useState<1 | 2>(1);
  const [fullName, setFullName] = React.useState('');
  const [timezone, setTimezone] = React.useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [authChecked, setAuthChecked] = React.useState(false);

  const timezoneOptions = React.useMemo(() => getTimezoneOptions(), []);

  // Auth guard — redirect if not a signed-in coach; prefill name from metadata
  React.useEffect(() => {
    (async () => {
      const sb = getSupabase();
      if (!sb) {
        window.location.replace('/coach/sign-in');
        return;
      }
      let { data: sessionWrap } = await sb.auth.getSession();
      if (!sessionWrap.session) {
        const hydrated = await hydrateSessionFromWorkerCookies(sb);
        if (hydrated) {
          sessionWrap = (await sb.auth.getSession()).data;
        }
      }
      const user = sessionWrap.session?.user ?? null;
      if (!user) {
        window.location.replace('/coach/sign-in');
        return;
      }
      const metaName =
        (user.user_metadata?.full_name as string | undefined) || (user.user_metadata?.name as string | undefined) || '';
      if (metaName) setFullName(metaName);
      setAuthChecked(true);
    })();
  }, []);

  // Fire a one-time success toast when the coach has just completed email
  // confirmation and been routed here by AuthCallback.finishConfirm. The
  // signal is a ?confirmed=1 URL param (set by AuthCallback); we strip it via
  // history.replaceState so a page reload or bookmark never refires the toast.
  React.useEffect(() => {
    if (!authChecked) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('confirmed') !== '1') return;
    toast.success("Email confirmed — let's finish setting up your account.");
    params.delete('confirmed');
    const qs = params.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`);
  }, [authChecked]);

  /**
   * Redeem the plan the coach picked on the marketing site, if any.
   *
   * Runs after onboarding is marked complete so a coach who abandons Stripe
   * Checkout still lands in a finished account on the free tier rather than
   * being trapped in onboarding forever. Checkout failures are deliberately
   * soft: we surface a toast and fall through to step 2, because a Stripe
   * hiccup must never block someone out of the product they just signed up for.
   *
   * @returns true if the browser is navigating to Checkout (caller should stop).
   */
  const redeemPlanIntent = async (): Promise<boolean> => {
    let intent: PlanIntent | null = null;
    try {
      const sb = getSupabase();
      const { data } = (await sb?.auth.getUser()) ?? { data: null };
      intent = planIntentFromUserMetadata(data?.user?.user_metadata as Record<string, unknown> | undefined);
    } catch {
      /* fall through to the localStorage copy */
    }
    // OAuth signups never hit the signUp() call that writes user_metadata.
    if (!intent) intent = consumeStoredPlanIntent();
    if (!intent) return false;

    const result = await startCheckout(intent);
    if (result.ok && result.url) {
      window.location.href = result.url;
      return true;
    }
    toast.error(result.message || 'Could not start checkout — you can upgrade any time from Settings.');
    return false;
  };

  const handleContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullName.trim()) {
      setError('Please enter your name');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const sb = getSupabase();
      if (!sb) throw new Error('Auth not configured');

      const { error: metaErr } = await sb.auth.updateUser({
        data: {
          full_name: fullName.trim(),
          name: fullName.trim(),
          timezone,
          onboarding_complete: true,
        },
      });
      if (metaErr) throw metaErr;
      capturePostHog('onboarding_completed', { timezone });

      // Best-effort: also update profiles.full_name via Worker (non-blocking; apiClient never rejects).
      void updateProfile({ name: fullName.trim() });

      // Push the refreshed session (now carrying onboarding_complete=true) to
      // the Worker cookie jar before step 2's dashboard links navigate, so
      // clicking "Explore the dashboard" doesn't flash the sign-in page.
      await syncWorkerSession();

      if (await redeemPlanIntent()) return;
      setStep(2);
    } catch (err: any) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = async () => {
    try {
      const sb = getSupabase();
      if (sb) await sb.auth.updateUser({ data: { onboarding_complete: true } });
    } catch (err) {
      // The skip still proceeds, but the coach will be shown onboarding again next sign-in.
      reportSwallowed('CoachOnboarding.handleSkip', err);
    }
    await syncWorkerSession();
    // Skipping the profile form is not skipping the plan they already chose.
    if (await redeemPlanIntent()) return;
    window.location.replace('/');
  };

  if (!authChecked) {
    return <div className="min-h-screen bg-[#0E0E10] grid place-items-center text-[#AEAEB0]">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-[#0E0E10] text-[#EDEDEF] flex items-center justify-center p-6">
      <div className="w-full max-w-md space-y-6">
        {/* Progress indicator */}
        <div className="flex justify-center gap-2">
          <div className={`h-1.5 w-10 rounded-full transition-colors ${step >= 1 ? 'bg-[#0A84FF]' : 'bg-[#2C2C2E]'}`} />
          <div className={`h-1.5 w-10 rounded-full transition-colors ${step >= 2 ? 'bg-[#0A84FF]' : 'bg-[#2C2C2E]'}`} />
        </div>

        {step === 1 ? (
          <>
            <div className="text-center">
              <h1 className="text-3xl font-bold mb-2">Set up your profile</h1>
              <p className="text-[#AEAEB0]">Quick setup before you start coaching</p>
            </div>

            <div className="bg-[#1C1C1E] rounded-xl p-6 border border-[#2C2C2E] space-y-5">
              {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-sm text-red-400">
                  {error}
                </div>
              )}

              <form onSubmit={handleContinue} className="space-y-4">
                <div>
                  <label htmlFor="fullName" className="block text-sm font-medium mb-2">
                    Full name
                  </label>
                  <input
                    id="fullName"
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Your name"
                    required
                    disabled={loading}
                    className="w-full rounded-lg border border-[#2C2C2E] bg-[#0E0E10] px-4 py-3 text-[#EDEDEF] placeholder-[#6E6E73] focus:border-[#0A84FF] focus:outline-none focus:ring-1 focus:ring-[#0A84FF] disabled:opacity-50"
                  />
                </div>

                <div>
                  <label htmlFor="timezone" className="block text-sm font-medium mb-2">
                    Timezone
                  </label>
                  <select
                    id="timezone"
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    disabled={loading}
                    className="w-full rounded-lg border border-[#2C2C2E] bg-[#0E0E10] px-4 py-3 text-[#EDEDEF] focus:border-[#0A84FF] focus:outline-none focus:ring-1 focus:ring-[#0A84FF] disabled:opacity-50"
                  >
                    {timezoneOptions.map((tz) => (
                      <option key={tz} value={tz}>
                        {formatTz(tz)}
                      </option>
                    ))}
                  </select>
                </div>

                <button
                  type="submit"
                  disabled={loading || !fullName.trim()}
                  className="w-full rounded-lg bg-[#0A84FF] px-4 py-3 font-medium text-white hover:bg-[#0A84FF]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? 'Saving…' : 'Continue'}
                </button>
              </form>

              <button
                onClick={handleSkip}
                className="w-full text-center text-sm text-[#6E6E73] hover:text-[#AEAEB0] transition-colors py-1"
              >
                Skip for now
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <CheckCircle className="w-12 h-12 text-green-400" />
              </div>
              <h1 className="text-3xl font-bold mb-2">You're all set!</h1>
              <p className="text-[#AEAEB0]">Here's what you can do next</p>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => window.location.replace('/clients/all')}
                className="w-full flex items-center gap-4 bg-[#1C1C1E] rounded-xl p-5 border border-[#2C2C2E] hover:border-[#0A84FF]/40 transition-colors text-left group"
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#0A84FF]/10 flex items-center justify-center">
                  <UserPlus className="w-5 h-5 text-[#0A84FF]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium">Invite your first client</p>
                  <p className="text-sm text-[#AEAEB0]">Send an invite link to get started</p>
                </div>
                <ArrowRight className="w-4 h-4 text-[#6E6E73] group-hover:text-[#0A84FF] transition-colors flex-shrink-0" />
              </button>

              <button
                onClick={() => window.location.replace('/programs/all')}
                className="w-full flex items-center gap-4 bg-[#1C1C1E] rounded-xl p-5 border border-[#2C2C2E] hover:border-purple-500/40 transition-colors text-left group"
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center">
                  <Dumbbell className="w-5 h-5 text-purple-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium">Build a program template</p>
                  <p className="text-sm text-[#AEAEB0]">Create reusable training programs</p>
                </div>
                <ArrowRight className="w-4 h-4 text-[#6E6E73] group-hover:text-purple-400 transition-colors flex-shrink-0" />
              </button>

              <button
                onClick={() => window.location.replace('/')}
                className="w-full flex items-center gap-4 bg-[#1C1C1E] rounded-xl p-5 border border-[#2C2C2E] hover:border-green-500/40 transition-colors text-left group"
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                  <LayoutDashboard className="w-5 h-5 text-green-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium">Explore the dashboard</p>
                  <p className="text-sm text-[#AEAEB0]">Get familiar with your workspace</p>
                </div>
                <ArrowRight className="w-4 h-4 text-[#6E6E73] group-hover:text-green-400 transition-colors flex-shrink-0" />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
